<template>
  <div class="br-empty-state" :class="[`br-empty-state--${size}`]">
    <div class="br-empty-state__icon-wrap" :class="`br-empty-state__icon-wrap--${variant}`">
      <v-icon :size="iconSize" :color="iconColor">{{ icon }}</v-icon>
    </div>
    <h3 class="br-empty-state__title">{{ title }}</h3>
    <p v-if="description" class="br-empty-state__description">{{ description }}</p>
    <div v-if="$slots.default" class="br-empty-state__actions">
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

type EmptyStateVariant = "default" | "brand" | "muted";
type EmptyStateSize = "sm" | "md" | "lg";

const iconColors: Record<EmptyStateVariant, string> = {
  default: "#242968",
  brand: "#e5183d",
  muted: "#9aa3c2",
};

export default defineComponent({
  name: "EmptyState",

  props: {
    icon: { type: String, default: "mdi-inbox-outline" },
    title: { type: String, required: true },
    description: { type: String, default: "" },
    variant: {
      type: String,
      default: "default",
      validator: (v: string) => ["default", "brand", "muted"].includes(v),
    },
    size: {
      type: String,
      default: "md",
      validator: (v: string) => ["sm", "md", "lg"].includes(v),
    },
  },

  computed: {
    iconColor(): string {
      return iconColors[this.variant as EmptyStateVariant] || iconColors.default;
    },
    iconSize(): number {
      const sizes: Record<EmptyStateSize, number> = { sm: 36, md: 48, lg: 56 };
      return sizes[this.size as EmptyStateSize] || 48;
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.br-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: $br-space-8 $br-space-4;
}

.br-empty-state--sm {
  padding: $br-space-6 $br-space-4;
}

.br-empty-state--lg {
  padding: $br-space-10 $br-space-4;
  min-height: 280px;
}

.br-empty-state__icon-wrap {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: $br-space-4;
}

.br-empty-state--sm .br-empty-state__icon-wrap {
  width: 64px;
  height: 64px;
  margin-bottom: $br-space-3;
}

.br-empty-state__icon-wrap--default {
  background: linear-gradient(145deg, rgba(36, 41, 104, 0.12), rgba(36, 41, 104, 0.04));
}

.br-empty-state__icon-wrap--brand {
  background: linear-gradient(145deg, rgba(229, 24, 61, 0.14), rgba(229, 24, 61, 0.05));
}

.br-empty-state__icon-wrap--muted {
  background: rgba(154, 163, 194, 0.12);
}

.br-empty-state__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: $br-ink-soft;
  line-height: 1.35;
}

.br-empty-state__description {
  margin: $br-space-2 0 0;
  font-size: $br-font-size-md;
  color: $br-muted;
  line-height: 1.5;
  max-width: 36ch;
}

.br-empty-state__actions {
  margin-top: $br-space-5;
  display: flex;
  flex-wrap: wrap;
  gap: $br-space-2;
  justify-content: center;
}
</style>
